import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { parseDateOnly } from '../data/sampleData';

// Legacy (grandfathered) clients for ONE territory, served by another AF#
// (Bettina, 2026-10-19). Admins and reviewers (2026-10-20). Each check pulls fresh AxisCare data for
// the territory's state and runs the decision tree over the chosen window; it
// is kept 60 days. On screen: full client names. Excel export: initials.

// Window choices (Bettina, 2026-10-27); 'initial_signing' = from the
// territory's Date Initial Agreement Signed in Zoho to today.
type WindowChoice = '60' | '90' | '120' | 'initial_signing';
const WINDOW_LABEL: Record<WindowChoice, string> = {
  '60': 'Last 60 days', '90': 'Last 90 days', '120': 'Last 120 days', initial_signing: 'From initial signing date (Zoho)',
};

interface Territory { af_number: string; status: string | null; state: string | null; zip_count: number }

interface CheckSummary {
  id: number;
  territory_af: string;
  window_choice?: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  stage_message: string | null;
  legacy_count: number | null;
  voided_count?: number | null;     // not grandfathered: a break in service (2026-11-03)
  pairs_evaluated: number | null;
  sites_total: number | null;
  sites_failed: string[];
  sites_included: string[] | null;   // null = check ran before the list was recorded
  error: string | null;
  created_at: string;
  completed_at: string | null;
  triggered_by: string | null;
}

interface CheckDetail extends CheckSummary {
  window_start: string | null;
  window_end: string | null;
  territory_effective_date: string | null;
  territory_effective_source: string | null;
  territory_zip_count: number | null;
}

interface ResultRow {
  id: number;
  client_name: string | null;
  home_zip: string | null;
  service_zip: string | null;
  servicing_af: string;
  start_of_care: string | null;
  days_grandfathered: number | null;
  last_visit: string | null;
  visits_in_window: number | null;
  client_status: string | null;
  note: string | null;
  grandfather_voided?: number;
}

const fmtDay = (v: string | null) => {
  const d = parseDateOnly(v);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
const fmtWhen = (v: string | null) => (v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export function LegacyClients() {
  const [territories, setTerritories] = useState<Territory[] | null>(null);
  const [picked, setPicked] = useState('');
  const [windowChoice, setWindowChoice] = useState<WindowChoice>('90');
  const [checks, setChecks] = useState<CheckSummary[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadChecks = () =>
    api<{ checks: CheckSummary[] }>('/api/legacy-checks')
      .then((d) => setChecks(d.checks))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load checks'));

  useEffect(() => {
    api<{ territories: Territory[] }>('/api/legacy-checks/territories')
      .then((d) => setTerritories(d.territories))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load territories'));
    loadChecks();
  }, []);

  // Poll while a check is pending/running.
  const active = checks?.find((c) => c.status === 'pending' || c.status === 'running');
  useEffect(() => {
    if (!active) return;
    const t = setInterval(loadChecks, 5000);
    return () => clearInterval(t);
  }, [active?.id, active?.status]);

  const run = async () => {
    if (!picked) return;
    setSubmitting(true); setError(null);
    try {
      const r = await api<{ id: number }>('/api/legacy-checks', { method: 'POST', body: { territory_af: picked, window: windowChoice } });
      await loadChecks();
      setOpenId(r.id);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
      setError(body?.error ?? (e instanceof Error ? e.message : 'Could not start the check'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Reference</div>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>Legacy clients</h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, maxWidth: 820 }}>
          Clients in one territory&apos;s zip codes, served by another AF#, that the decision tree grandfathers:
          start of care before that territory&apos;s own effective date, with no break that voids it. Each check
          pulls fresh AxisCare data for the territory&apos;s state (a few minutes; it waits for any running audit),
          then looks at the window you choose (60, 90 or 120 days, or since the territory's initial signing date in Zoho).
          Checks are kept 60 days. The Excel export shows client initials only.
        </div>
      </div>

      <div className="card card-pad" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span className="muted">Territory (AF#)</span>
          <select value={picked} onChange={(e) => setPicked(e.target.value)} style={{ minWidth: 260 }} disabled={!territories}>
            <option value="">{territories ? 'Choose a territory…' : 'Loading…'}</option>
            {territories?.map((t) => (
              <option key={t.af_number} value={t.af_number}>
                {t.af_number} · {t.state ?? '—'} · {t.status ?? '—'} · {t.zip_count} zips
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span className="muted">Window</span>
          <select value={windowChoice} onChange={(e) => setWindowChoice(e.target.value as WindowChoice)} style={{ minWidth: 220 }}>
            {(Object.keys(WINDOW_LABEL) as WindowChoice[]).map((w) => <option key={w} value={w}>{WINDOW_LABEL[w]}</option>)}
          </select>
        </label>
        {windowChoice === 'initial_signing' && (
          <span className="muted" style={{ fontSize: 11.5, maxWidth: 300 }}>
            Can cover years of visits for an older territory, so this check pulls much more from AxisCare and takes longer.
          </span>
        )}
        <button className="btn btn-primary" onClick={run} disabled={!picked || submitting || !!active}>
          <Icon name="play" size={12} /> {submitting ? 'Queueing…' : active ? 'A check is running…' : 'Run check'}
        </button>
      </div>

      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div className="card">
        <div className="sec-head"><h3>Checks · last 60 days</h3></div>
        {!checks && <div className="card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
        {checks && checks.length === 0 && <div className="card-pad muted" style={{ fontSize: 13 }}>No checks yet. Choose a territory and run one.</div>}
        {checks && checks.length > 0 && (
          <table className="tbl">
            <thead><tr>
              <th>#</th><th>Territory</th><th>Window</th><th>Status</th><th style={{ textAlign: 'right' }}>Legacy clients</th>
              <th>Run</th><th>By</th><th>Details</th>
            </tr></thead>
            <tbody>
              {checks.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setOpenId(c.id)}>
                  <td className="mono" style={{ fontSize: 11.5 }}>{c.id}</td>
                  <td className="mono">{c.territory_af}</td>
                  <td style={{ fontSize: 12 }}>{WINDOW_LABEL[(c.window_choice ?? '90') as WindowChoice] ?? c.window_choice}</td>
                  <td className={c.status === 'failed' ? 'c-warn' : c.status === 'complete' ? '' : 'muted'}>{c.status}</td>
                  <td className="num mono" style={{ textAlign: 'right' }}>
                    {c.legacy_count ?? '—'}
                    {!!c.voided_count && <span className="c-warn" title="Not grandfathered: a break in service" style={{ fontSize: 11 }}> +{c.voided_count} break</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{fmtWhen(c.created_at)}</td>
                  <td style={{ fontSize: 12 }}>{c.triggered_by ?? '—'}</td>
                  <td style={{ fontSize: 12 }} className={c.sites_failed.length || c.error ? 'c-warn' : 'muted'}>
                    {c.error ?? c.stage_message ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId != null && <CheckModal id={openId} onClose={() => setOpenId(null)} live={active?.id === openId} />}
    </div>
  );
}

function CheckModal({ id, onClose, live }: { id: number; onClose: () => void; live: boolean }) {
  const [data, setData] = useState<{ check: CheckDetail; rows: ResultRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = () => api<{ check: CheckDetail; rows: ResultRow[] }>(`/api/legacy-checks/${id}`)
      .then(setData).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
    load();
    if (!live) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id, live]);

  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`/api/legacy-checks/${id}/export.xlsx`, { credentials: 'include' });
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legacy-clients-${data?.check.territory_af ?? id}-check${id}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const c = data?.check;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 8, padding: 16, width: '92vw', maxWidth: 1250, maxHeight: '88vh', overflow: 'auto', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              Legacy clients in <span className="mono">{c?.territory_af ?? '…'}</span>&apos;s territory · check #{id}
            </h3>
            {c && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>
                Territory effective date <strong>{fmtDay(c.territory_effective_date)}</strong>
                {c.territory_effective_source ? <> (from {c.territory_effective_source})</> : null}
                {' '}· {c.territory_zip_count ?? '—'} zips
                {c.window_start && <> · window ({WINDOW_LABEL[(c.window_choice ?? '90') as WindowChoice] ?? c.window_choice}) {fmtDay(c.window_start)} – {fmtDay(c.window_end)}</>}
              </div>
            )}
            {c && c.sites_total != null && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6, maxWidth: 900 }}>
                <strong>Sites included ({c.sites_total}):</strong>{' '}
                {c.sites_included
                  ? c.sites_included.map((s, i) => (
                      <span key={s}>
                        {i > 0 && ', '}
                        <span className={`mono ${c.sites_failed.includes(s) ? 'c-warn' : ''}`} title={c.sites_failed.includes(s) ? 'Failed to refresh' : undefined}>{s}</span>
                      </span>
                    ))
                  : <span>list not recorded (check ran before 20 Oct 2026)</span>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={exportXlsx} disabled={c?.status !== 'complete' || downloading}>
              <Icon name="download" size={12} /> {downloading ? 'Exporting…' : 'Export Excel (initials)'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
          </div>
        </div>

        {error && <div className="c-warn" style={{ fontSize: 13 }}>{error}</div>}
        {c && (c.status === 'pending' || c.status === 'running') && (
          <div className="card card-pad muted" style={{ fontSize: 13 }}>{c.status === 'pending' ? 'Queued — waiting for any running audit to finish.' : (c.stage_message ?? 'Running…')}</div>
        )}
        {c?.status === 'failed' && <div className="card card-pad c-warn" style={{ fontSize: 13 }}>Check failed: {c.error}</div>}
        {c && c.sites_failed.length > 0 && (
          <div className="card card-pad c-warn" style={{ fontSize: 12.5, marginBottom: 10 }}>
            ⚠️ Data incomplete: these sites failed to refresh, so their clients were judged on older data: {c.sites_failed.join(', ')}.
          </div>
        )}
        {c?.status === 'complete' && data && data.rows.length === 0 && (
          <div className="card card-pad muted" style={{ fontSize: 13 }}>No grandfathered clients (and none disqualified by a break in service) served by another AF# in this territory in the window.</div>
        )}
        {c?.status === 'complete' && data && data.rows.some((r) => r.grandfather_voided) && (
          <div className="card card-pad c-warn" style={{ fontSize: 12.5, marginBottom: 10 }}>
            {data.rows.filter((r) => r.grandfather_voided).length} client(s) started care before the territory date but had a break in
            service, so they are <strong>not</strong> grandfathered. They are listed last, marked "No – break"; the note gives the gap.
          </div>
        )}
        {c?.status === 'complete' && data && data.rows.length > 0 && (
          <table className="tbl">
            <thead><tr>
              <th>Client</th><th>Grandfathered</th><th>Home zip</th><th>Service zip</th><th>Servicing AF#</th><th>Start of care</th>
              <th style={{ textAlign: 'right' }}>Days grandfathered</th><th>Last visit</th>
              <th style={{ textAlign: 'right' }}>Visits (window)</th><th>Status</th><th>Note</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.client_name ?? <span className="muted">(no name on file)</span>}</td>
                  <td className={r.grandfather_voided ? 'c-warn' : ''}>{r.grandfather_voided ? 'No – break' : 'Yes'}</td>
                  <td className="mono">{r.home_zip ?? '—'}</td>
                  <td className="mono">{r.service_zip ?? '—'}</td>
                  <td className="mono">{r.servicing_af}</td>
                  <td>{fmtDay(r.start_of_care)}</td>
                  <td className="num mono" style={{ textAlign: 'right' }}>{r.days_grandfathered ?? '—'}</td>
                  <td>{r.last_visit ? new Date(r.last_visit).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                  <td className="num mono" style={{ textAlign: 'right' }}>{r.visits_in_window ?? '—'}</td>
                  <td>{r.client_status ?? '—'}</td>
                  <td className={r.grandfather_voided ? 'c-warn' : 'muted'} style={{ fontSize: 11.5 }}>{r.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
